import { DIFFICULTY } from '@/config/difficulty';
import { GAME } from '@/config/game';
import { sectorAt } from '@/config/sectors';
import type { Rng } from '@/core/rng';

/** What the rotation needs to know about a boss (BossDef subset). */
export interface RotationEntry {
  readonly id: string;
  readonly tier: number;
  readonly minRoom?: number;
  readonly finale?: boolean;
}

/** A drawn fight: which boss, at which level. */
export interface FightPick {
  id: string;
  level: number;
}

/**
 * Rotation state as stored in the run save (core/save.ts). Field names
 * match SaveData; every field is optional so old saves still load.
 */
export interface RotationSave {
  /** Last fights, newest last — no immediate repeats across a refill */
  recentBossIds?: string[];
  /** Encounters per boss id this run (the old per-boss ladder value) */
  bossNextLevel?: Record<string, number>;
  /** Regular bosses still unseen in the current bag cycle */
  bossBag?: string[];
}

/**
 * Endless boss level from run DEPTH (docs/01). Together with
 * `sectorBossLevel` the ONE place where progress becomes a level.
 * @param depth rooms cleared so far (0 = first fight)
 * @param encounters earlier fights against this boss in the run
 */
export function bossLevelFor(depth: number, encounters: number): number {
  const c = GAME.run.bossLevel;
  return 1 + Math.floor(Math.max(0, depth) * c.perRoom) +
    Math.min(c.encounterCap, encounters) * c.perEncounter;
}

/**
 * Sector boss level (EPIC D2): the sector's base + its per-room ramp; the
 * finale adds its modifier levels (→ extra evolve tiers). docs/01.
 */
export function sectorBossLevel(sector: number, room: number, finale: boolean): number {
  const c = DIFFICULTY.sectors[sector]!.bossLevel;
  const finaleLevels = finale ? sectorAt(sector).finaleMod.levels : 0;
  return c.base + Math.floor(Math.max(0, room) * c.perRoom) + finaleLevels;
}

/** Endless finale rooms (Warden) — pure function of the room, previewable. */
export function isFinaleRoom(room: number): boolean {
  const every = GAME.run.rotation.finaleEvery;
  return every > 0 && (room + 1) % every === 0;
}

/** Def-tier the regular draw leans toward at this depth. */
export function tierTarget(room: number): number {
  const r = GAME.run.rotation;
  return Math.min(r.tierTargetMax, r.tierTargetStart + room * r.tierTargetPerRoom);
}

/** What differs between the endless rotation and a sector's. */
export interface RotationRules {
  isFinale(room: number): boolean;
  level(room: number, encounters: number, finale: boolean): number;
  /** Def-tier the regular draw leans toward in `room` */
  tierTarget(room: number): number;
}

/** Endless: periodic finales, depth levels, the global tier ramp. */
export const ENDLESS_RULES: RotationRules = {
  isFinale: isFinaleRoom,
  level: (room, encounters) => bossLevelFor(room, encounters),
  tierTarget,
};

/**
 * Sector rules: the finale is room `rooms`, levels come from the sector
 * table, and the tier target sweeps the pool's own tier span across the
 * regular rooms (low tiers open the sector, heavy ones close it).
 */
export function sectorRules(sector: number, pool: ReadonlyArray<RotationEntry>): RotationRules {
  const def = sectorAt(sector);
  const tiers = pool.filter((e) => !e.finale).map((e) => e.tier);
  const lo = Math.min(...tiers);
  const hi = Math.max(...tiers);
  const span = Math.max(1, def.rooms - 1);
  return {
    isFinale: (room) => room >= def.rooms,
    level: (room, _enc, finale) => sectorBossLevel(sector, room, finale),
    tierTarget: (room) => lo + ((hi - lo) * Math.min(room, span)) / span,
  };
}

/**
 * Seeded shuffle-bag boss rotation (docs/01). Every regular boss is fought
 * once per cycle (gates permitting) before any of them repeats; the order
 * inside a cycle is a weighted draw that favors the room's tier target.
 * Finale bosses own the finale rooms (`rules`: endless or one sector).
 */
export class BossRotation {
  private bag: string[] = [];
  private recent: string[] = [];
  private seen: Record<string, number> = {};
  private readonly regular: RotationEntry[];
  private readonly finales: RotationEntry[];

  constructor(pool: ReadonlyArray<RotationEntry>, private readonly rules = ENDLESS_RULES) {
    this.regular = pool.filter((e) => !e.finale);
    this.finales = pool.filter((e) => e.finale);
    this.reset();
  }

  /** Fresh run: full bag, no history. */
  reset(): void {
    this.bag = this.regular.map((e) => e.id);
    this.recent = [];
    this.seen = {};
  }

  /** Load from a save; saves without a bag get unseen bosses first. */
  restore(save: RotationSave): void {
    this.recent = [...(save.recentBossIds ?? [])];
    this.seen = { ...(save.bossNextLevel ?? {}) };
    const known = new Set(this.regular.map((e) => e.id));
    this.bag = save.bossBag
      ? save.bossBag.filter((id) => known.has(id))
      : this.regular.filter((e) => !this.seen[e.id]).map((e) => e.id);
  }

  snapshot(): Required<RotationSave> {
    return {
      recentBossIds: [...this.recent],
      bossNextLevel: { ...this.seen },
      bossBag: [...this.bag],
    };
  }

  /** Draw the fight for `room` and advance bag, history and encounters. */
  next(rng: Rng, room: number): FightPick {
    const finale = this.rules.isFinale(room) ? this.pickFinale(room) : null;
    const entry = finale ?? this.drawRegular(rng, room);
    const encounters = this.seen[entry.id] ?? 0;
    this.seen[entry.id] = encounters + 1;
    this.recent.push(entry.id);
    while (this.recent.length > GAME.run.rotation.noRepeatWindow) this.recent.shift();
    return { id: entry.id, level: this.rules.level(room, encounters, finale !== null) };
  }

  /**
   * Least-fought finale boss whose gate is open (deterministic); ties go to
   * the lower def-tier, so Warden opens and Avatar alternates with it.
   */
  private pickFinale(room: number): RotationEntry | null {
    let best: RotationEntry | null = null;
    for (const e of this.finales) {
      if (room < (e.minRoom ?? 0)) continue;
      const seen = this.seen[e.id] ?? 0;
      const bestSeen = best ? this.seen[best.id] ?? 0 : Infinity;
      if (seen < bestSeen || (seen === bestSeen && best !== null && e.tier < best.tier)) best = e;
    }
    return best;
  }

  private drawRegular(rng: Rng, room: number): RotationEntry {
    let pool = this.candidates(room, true);
    if (pool.length === 0) {
      // Cycle done (or the leftovers are still gated): every boss is back
      this.bag = this.regular.map((e) => e.id);
      pool = this.candidates(room, true);
    }
    // Tiny pools: the no-repeat window yields — the room gates never do
    if (pool.length === 0) pool = this.candidates(room, false);
    if (pool.length === 0) pool = [this.earliest()];
    const picked = this.weighted(rng, pool, room);
    const at = this.bag.indexOf(picked.id);
    if (at >= 0) this.bag.splice(at, 1);
    return picked;
  }

  private candidates(room: number, avoidRecent: boolean): RotationEntry[] {
    return this.regular.filter((e) =>
      this.bag.includes(e.id) &&
      room >= (e.minRoom ?? 0) &&
      !(avoidRecent && this.recent.includes(e.id)));
  }

  /** Fallback that still honors the gates: the earliest-unlocking boss. */
  private earliest(): RotationEntry {
    return this.regular.reduce((a, b) => ((b.minRoom ?? 0) < (a.minRoom ?? 0) ? b : a));
  }

  private weighted(rng: Rng, pool: ReadonlyArray<RotationEntry>, room: number): RotationEntry {
    const target = this.rules.tierTarget(room);
    const k = GAME.run.rotation.tierFalloff;
    const weight = (e: RotationEntry): number => Math.exp(-k * Math.abs(e.tier - target));
    let total = 0;
    for (const e of pool) total += weight(e);
    let r = rng.next() * total;
    for (const e of pool) {
      r -= weight(e);
      if (r < 0) return e;
    }
    return pool[pool.length - 1]!;
  }
}
